import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Badge,
  OutlinedInput,
  Divider,
} from "@mui/material";
import styled from "styled-components";

// Import services and utilities
import { getAllUrls, deleteUrl } from "../services/airtable";
import { extractDomain } from "../utils/urlShortener";

// Import icons
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BarChartIcon from "@mui/icons-material/BarChart";
import PublicIcon from "@mui/icons-material/Public";
import CategoryIcon from "@mui/icons-material/Category";
import SourceIcon from "@mui/icons-material/Source";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

// ==========================================
// PAGINATION CONFIG
// ==========================================
const ITEMS_PER_PAGE = 20;

// Styled components
const SearchBox = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
`;

const TableWrapper = styled.div`
  margin-top: 16px;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StyledTableRow = styled(TableRow)`
  &:hover {
    background-color: #f5f5f5;
  }
`;

const FilterPanel = styled.div`
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const DateInput = styled(TextField)`
  & input[type="date"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
  }
`;

const ClickCounter = styled.div`
  display: flex;
  align-items: center;
  background-color: #f0f0f0;
  border-radius: 16px;
  padding: 2px 8px;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const MetaTag = styled(Chip)`
  margin-right: 4px;
  margin-bottom: 4px;
  font-size: 11px;
  height: 22px;
`;

const DetailSection = styled.div`
  margin-bottom: 16px;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  margin-bottom: 8px;
  align-items: center;
`;

const DetailLabel = styled.span`
  font-weight: 500;
  min-width: 120px;
`;

const DetailValue = styled.div`
  flex: 1;
  word-break: break-word;
  display: flex;
  align-items: center;
`;

// ==========================================
// PAGINATION STYLED COMPONENTS
// ==========================================
const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #e0e0e0;
  background-color: #fafafa;
`;

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const PageButton = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: none;
  background-color: ${(props) => (props.$active ? "#1976d2" : "transparent")};
  color: ${(props) => (props.$active ? "#fff" : props.disabled ? "#bbb" : "#555")};
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? "600" : "500")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  user-select: none;

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.$active ? "#1565c0" : "#e8e8e8")};
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid #ddd;
  background-color: #fff;
  color: ${(props) => (props.disabled ? "#bbb" : "#555")};
  border-radius: 8px;
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s ease;
  user-select: none;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
    border-color: #bbb;
  }
`;

const PaginationInfo = styled.span`
  color: #888;
  font-size: 13px;
  white-space: nowrap;
`;

const UrlTable = () => {
  const queryClient = useQueryClient();

  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [urlToDelete, setUrlToDelete] = useState(null);
  const [showCopied, setShowCopied] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState("");
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedPics, setSelectedPics] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Options for filters
  const countryOptions = ["Malaysia", "Singapore", "Cambodia"];

  // Fetch all URLs - they're already filtered in the backend to exclude deleted ones
  const {
    data: urls = [],
    isLoading,
    isError,
    refetch,
  } = useQuery("urls", getAllUrls, {
    // Add error handling options
    retry: 1,
    onError: (error) => {
      console.error("Error in URL table query:", error);
    },
    // Refresh data every 30 seconds to update click counts
    refetchInterval: 30000,
  });

  // Get unique PICs from data
  const picOptions = React.useMemo(() => {
    if (!urls || urls.length === 0) return [];

    const pics = urls.map((url) => url.pic).filter(Boolean);
    return [...new Set(pics)].sort();
  }, [urls]);

  // Get unique channels from data
  const channelOptions = React.useMemo(() => {
    if (!urls || urls.length === 0) return [];

    const channels = urls.map((url) => url.channel).filter(Boolean);
    return [...new Set(channels)].sort();
  }, [urls]);

  // Get unique sources from data
  const sourcesOptions = React.useMemo(() => {
    if (!urls || urls.length === 0) return [];

    const sources = urls.map((url) => url.sources).filter(Boolean);
    return [...new Set(sources)].sort();
  }, [urls]);

  // Delete URL mutation
  const deleteUrlMutation = useMutation(deleteUrl, {
    onSuccess: () => {
      queryClient.invalidateQueries("urls");
      setDeleteDialogOpen(false);
      setUrlToDelete(null);
    },
  });

  // Handle copying URL
  const handleCopy = (shortUrl, event) => {
    // Stop propagation to prevent row click
    if (event) {
      event.stopPropagation();
    }
    
    navigator.clipboard
      .writeText(shortUrl)
      .then(() => {
        setCopiedUrl(shortUrl);
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      })
      .catch((err) => console.error("Failed to copy URL:", err));
  };

  // Handle delete confirmation
  const handleDeleteClick = (url, event) => {
    // Stop propagation to prevent row click
    if (event) {
      event.stopPropagation();
    }
    
    setUrlToDelete(url);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (urlToDelete) {
      deleteUrlMutation.mutate(urlToDelete.id);
    }
  };

  // Handle row click to open detail modal
  const handleRowClick = (url) => {
    setSelectedUrl(url);
    setDetailModalOpen(true);
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedCountries([]);
    setSelectedChannels([]);
    setSelectedSources([]);
    setSelectedPics([]);
    setDateFrom("");
    setDateTo("");
  };

  // Check if any filters are active
  const isFilterActive = 
    selectedCountries.length > 0 || 
    selectedChannels.length > 0 || 
    selectedSources.length > 0 || 
    selectedPics.length > 0 || 
    dateFrom || 
    dateTo;

  // Convert string date to JavaScript Date object
  const parseDate = (dateString) => {
    if (!dateString) return null;

    try {
      // For dates like "27/2/2025 9:43pm"
      const parts = dateString.split(" ");
      if (parts.length === 2) {
        const datePart = parts[0]; // e.g., "27/2/2025"
        const datePieces = datePart.split("/");
        if (datePieces.length === 3) {
          const day = parseInt(datePieces[0], 10);
          const month = parseInt(datePieces[1], 10) - 1; // JS months are 0-indexed
          const year = parseInt(datePieces[2], 10);

          return new Date(year, month, day);
        }
      }

      // Fallback to standard date parsing
      return new Date(dateString);
    } catch (error) {
      console.error("Error parsing date:", error);
      return null;
    }
  };

  // Filter URLs by search term and active filters
  const filteredUrls = urls.filter((url) => {
    // First filter by search term
    const matchesSearch =
      searchTerm === "" ||
      (url.originalurl &&
        url.originalurl.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (url.shortCode &&
        url.shortCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (url.campaignname &&
        url.campaignname.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!matchesSearch) return false;

    // Filter by country if selected
    if (
      selectedCountries.length > 0 &&
      (!url.country || !selectedCountries.includes(url.country))
    ) {
      return false;
    }

    // Filter by channel if selected
    if (
      selectedChannels.length > 0 &&
      (!url.channel || !selectedChannels.includes(url.channel))
    ) {
      return false;
    }

    // Filter by sources if selected
    if (
      selectedSources.length > 0 &&
      (!url.sources || !selectedSources.includes(url.sources))
    ) {
      return false;
    }

    // Filter by PIC if selected
    if (
      selectedPics.length > 0 &&
      (!url.pic || !selectedPics.includes(url.pic))
    ) {
      return false;
    }

    // Filter by date range if set
    if (dateFrom || dateTo) {
      const urlDate = parseDate(url.createdtime);

      if (!urlDate) return false;

      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        if (urlDate < fromDate) return false;
      }

      if (dateTo) {
        const toDate = new Date(dateTo);
        // Set time to end of day for inclusive filtering
        toDate.setHours(23, 59, 59, 999);
        if (urlDate > toDate) return false;
      }
    }

    // If it passed all filters, include it
    return true;
  });

  // ==========================================
  // PAGINATION LOGIC
  // ==========================================
  const totalPages = Math.ceil(filteredUrls.length / ITEMS_PER_PAGE);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCountries, selectedChannels, selectedSources, selectedPics, dateFrom, dateTo]);

  // Also clamp currentPage if it exceeds totalPages (e.g. after deleting last item on last page)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Slice the filtered URLs for current page
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUrls = filteredUrls.slice(startIndex, endIndex);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Max page buttons to show

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  // Format date for display - UPDATED
  const formatDisplayDate = (dateString) => {
    if (!dateString) return "-";

    try {
      // Check if it's already in a user-friendly format (like "27/2/2025 9:43pm")
      const parts = dateString.split(" ");
      if (parts.length === 2 && parts[0].includes("/") && 
         (parts[1].toLowerCase().includes("am") || parts[1].toLowerCase().includes("pm"))) {
        // Already in a good format, return as is
        return dateString;
      }

      // Handle ISO format or timestamp format
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original if we can't parse it
      }

      // Format: "15 April 2025, 5:07pm"
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'long' });
      const year = date.getFullYear();
      
      // Format time in 12-hour with am/pm
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12; // Convert 0 to 12 for 12am
      
      return `${day} ${month} ${year}, ${hours}:${minutes}${ampm}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Return original as fallback
    }
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <TableBody>
      {Array.from(new Array(5)).map((_, index) => (
        <TableRow key={index}>
          <TableCell>
            <Skeleton variant="text" />
            <Skeleton variant="text" width="30%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="60%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="20%" />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width="30%" />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <>
      {/* Search and filter box */}
      <SearchBox>
        <TextField
          placeholder="Search URLs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
            ),
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={() => setSearchTerm("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />

        <Badge color="primary" variant="dot" invisible={!isFilterActive}>
          <Button
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? "contained" : "outlined"}
            size="small"
            color={isFilterActive ? "primary" : "inherit"}
          >
            Filters
          </Button>
        </Badge>
      </SearchBox>

      {/* Filter panel */}
      {showFilters && (
        <FilterPanel>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            mb={2}
            flexWrap="wrap"
          >
            {/* Country filter */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="country-filter-label">Filter by Country</InputLabel>
              <Select
                labelId="country-filter-label"
                id="country-filter"
                multiple
                value={selectedCountries}
                onChange={(e) => setSelectedCountries(e.target.value)}
                input={<OutlinedInput label="Filter by Country" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                    },
                  },
                }}
                startAdornment={
                  <PublicIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
                }
              >
                {countryOptions.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Channel filter */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="channel-filter-label">Filter by Channel</InputLabel>
              <Select
                labelId="channel-filter-label"
                id="channel-filter"
                multiple
                value={selectedChannels}
                onChange={(e) => setSelectedChannels(e.target.value)}
                input={<OutlinedInput label="Filter by Channel" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                    },
                  },
                }}
                startAdornment={
                  <CategoryIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
                }
              >
                {channelOptions.map((channel) => (
                  <MenuItem key={channel} value={channel}>
                    {channel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Sources filter */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="sources-filter-label">Filter by Sources</InputLabel>
              <Select
                labelId="sources-filter-label"
                id="sources-filter"
                multiple
                value={selectedSources}
                onChange={(e) => setSelectedSources(e.target.value)}
                input={<OutlinedInput label="Filter by Sources" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                    },
                  },
                }}
                startAdornment={
                  <SourceIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
                }
              >
                {sourcesOptions.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* PIC filter */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel id="pic-filter-label">Filter by PIC</InputLabel>
              <Select
                labelId="pic-filter-label"
                id="pic-filter"
                multiple
                value={selectedPics}
                onChange={(e) => setSelectedPics(e.target.value)}
                input={<OutlinedInput label="Filter by PIC" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 224,
                    },
                  },
                }}
              >
                {picOptions.map((pic) => (
                  <MenuItem key={pic} value={pic}>
                    {pic}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date range filter */}
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <DateInput
                label="From Date"
                type="date"
                size="small"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarTodayIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                  ),
                }}
              />

              <DateInput
                label="To Date"
                type="date"
                size="small"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarTodayIcon
                      fontSize="small"
                      sx={{ mr: 1, color: "text.secondary" }}
                    />
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Reset filters button */}
          <Box display="flex" justifyContent="flex-end">
            <Button
              onClick={resetFilters}
              size="small"
              disabled={!isFilterActive}
              color="inherit"
            >
              Reset Filters
            </Button>
          </Box>
        </FilterPanel>
      )}

      {/* URL Count Display - UPDATED with pagination info */}
      {!isLoading && urls.length > 0 && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mb: 2, fontSize: "14px" }}
        >
          {isFilterActive || searchTerm ? (
            filteredUrls.length === 0 ? (
              `No URLs match your filters (${urls.length} total available)`
            ) : (
              `Showing ${startIndex + 1}–${Math.min(endIndex, filteredUrls.length)} of ${filteredUrls.length} filtered URLs (${urls.length} total)`
            )
          ) : (
            `Showing ${startIndex + 1}–${Math.min(endIndex, filteredUrls.length)} of ${filteredUrls.length} URLs`
          )}
        </Typography>
      )}

      {/* URL Table */}
      <TableWrapper>
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Short URL</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>PIC</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            {isLoading ? (
              renderSkeleton()
            ) : isError ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="error" align="center">
                      Error loading URLs. Please try again.
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : filteredUrls.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography align="center" color="textSecondary">
                      {isFilterActive
                        ? "No URLs match your filters."
                        : searchTerm
                        ? "No URLs match your search."
                        : "No shortened URLs yet. Create your first one!"}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {/* CHANGED: render paginatedUrls instead of filteredUrls */}
                {paginatedUrls.map((url) => (
                  <StyledTableRow 
                    key={url.id}
                    onClick={() => handleRowClick(url)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Typography variant="body2" fontWeight="500">
                          {url.fullshorturl ||
                            `${window.location.origin}/${url.shortCode}`}
                        </Typography>
                        <ClickCounter>
                          <BarChartIcon
                            fontSize="inherit"
                            sx={{ mr: 0.5, fontSize: "14px" }}
                          />
                          {url.clicks || 0} clicks
                        </ClickCounter>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {url.campaignname || "-"}
                      </Typography>
                      <Box mt={0.5}>
                        {url.channel && (
                          <MetaTag 
                            label={url.channel} 
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}
                        {url.sources && (
                          <MetaTag 
                            label={url.sources} 
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                        {url.country && (
                          <MetaTag 
                            label={url.country} 
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{url.pic || "-"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Copy URL">
                        <IconButton
                          size="small"
                          onClick={(e) =>
                            handleCopy(
                              url.fullshorturl ||
                                `${window.location.origin}/${url.shortCode}`,
                              e
                            )
                          }
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete URL">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => handleDeleteClick(url, e)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </TableContainer>

        {/* ==========================================
            PAGINATION CONTROLS
            ========================================== */}
        {!isLoading && filteredUrls.length > ITEMS_PER_PAGE && (
          <PaginationContainer>
            {/* Previous button */}
            <NavButton
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <NavigateBeforeIcon sx={{ fontSize: 18 }} />
              Previous
            </NavButton>

            {/* Page numbers */}
            <PaginationWrapper>
              {getPageNumbers().map((page, index) =>
                page === "..." ? (
                  <span
                    key={`ellipsis-${index}`}
                    style={{
                      padding: "0 4px",
                      color: "#999",
                      fontSize: "13px",
                      userSelect: "none",
                    }}
                  >
                    …
                  </span>
                ) : (
                  <PageButton
                    key={page}
                    $active={currentPage === page}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </PageButton>
                )
              )}
            </PaginationWrapper>

            {/* Next button */}
            <NavButton
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
              <NavigateNextIcon sx={{ fontSize: 18 }} />
            </NavButton>
          </PaginationContainer>
        )}
      </TableWrapper>

      {/* URL Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedUrl && (
          <>
            <DialogTitle sx={{ borderBottom: "1px solid #e0e0e0", pb: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">{selectedUrl.campaignname || "URL Details"}</Typography>
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={() => setDetailModalOpen(false)}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
              {/* URL Information Section */}
              <DetailSection>
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                  URL Information
                </Typography>
                
                <DetailRow>
                  <DetailLabel>Short URL:</DetailLabel>
                  <DetailValue>
                    {selectedUrl.fullshorturl || `${window.location.origin}/${selectedUrl.shortCode}`}
                    <IconButton 
                      size="small" 
                      sx={{ ml: 1 }}
                      onClick={() => handleCopy(
                        selectedUrl.fullshorturl || 
                        `${window.location.origin}/${selectedUrl.shortCode}`
                      )}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Original URL:</DetailLabel>
                  <DetailValue>
                    {selectedUrl.originalurl || "-"}
                    <IconButton 
                      size="small" 
                      sx={{ ml: 1 }}
                      onClick={() => handleCopy(selectedUrl.originalurl)}
                      disabled={!selectedUrl.originalurl}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Created:</DetailLabel>
                  <DetailValue>
                    <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                    {formatDisplayDate(selectedUrl.createdtime) || "-"}
                  </DetailValue>
                </DetailRow>
              </DetailSection>

              {/* Campaign Information Section */}
              <DetailSection>
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                  Campaign Information
                </Typography>
                
                <DetailRow>
                  <DetailLabel>PIC:</DetailLabel>
                  <DetailValue>
                    <PersonIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                    {selectedUrl.pic || "-"}
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Channel:</DetailLabel>
                  <DetailValue>
                    <CategoryIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                    {selectedUrl.channel || "-"}
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Sources:</DetailLabel>
                  <DetailValue>
                    <SourceIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                    {selectedUrl.sources || "-"}
                  </DetailValue>
                </DetailRow>
                
                <DetailRow>
                  <DetailLabel>Country:</DetailLabel>
                  <DetailValue>
                    <PublicIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                    {selectedUrl.country || "-"}
                  </DetailValue>
                </DetailRow>
              </DetailSection>

              {/* Analytics Section */}
              <DetailSection>
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                  Analytics
                </Typography>
                
                <DetailRow>
                  <DetailLabel>Total Clicks:</DetailLabel>
                  <DetailValue>
                    <BarChartIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                    {selectedUrl.clicks || "0"}
                  </DetailValue>
                </DetailRow>
              </DetailSection>

              {/* UTM URL Section (if available) */}
              {selectedUrl.fullutmurl && (
                <DetailSection>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    UTM Parameters
                  </Typography>
                  
                  <DetailRow>
                    <DetailLabel>Full UTM URL:</DetailLabel>
                    <DetailValue>
                      {selectedUrl.fullutmurl}
                      <IconButton 
                        size="small" 
                        sx={{ ml: 1 }}
                        onClick={() => handleCopy(selectedUrl.fullutmurl)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </DetailValue>
                  </DetailRow>
                </DetailSection>
              )}
            </DialogContent>
            <DialogActions sx={{ borderTop: "1px solid #e0e0e0", px: 3, py: 2 }}>
              <Button 
                variant="outlined" 
                onClick={() => handleCopy(
                  selectedUrl.fullshorturl || 
                  `${window.location.origin}/${selectedUrl.shortCode}`
                )}
                startIcon={<ContentCopyIcon />}
              >
                Copy Short URL
              </Button>
              <Button 
                variant="contained" 
                onClick={() => setDetailModalOpen(false)}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

     {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this shortened URL?
          </Typography>
          {urlToDelete && (
            <Typography variant="body2" color="textSecondary" mt={1}>
              Short URL:{" "}
              {urlToDelete.fullshorturl ||
                `${window.location.origin}/${urlToDelete.shortCode}`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleteUrlMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            disabled={deleteUrlMutation.isLoading}
          >
            {deleteUrlMutation.isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copied notification */}
      <Snackbar
        open={showCopied}
        autoHideDuration={2000}
        onClose={() => setShowCopied(false)}
      >
        <Alert severity="success">URL copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
};

export default UrlTable;
